import type {
  FetchPublicWidgetConfigInput,
  FetchPublicWidgetConfigResult,
} from "../api/widget-api-client.ts";
import { fetchPublicWidgetConfig } from "../api/widget-api-client.ts";
import type { RuntimeWidgetConfig } from "../config/public-config.types.ts";
import { parseWidgetEnv, type WidgetEnvInput } from "../env/widget-env.ts";
import { createWidgetStore, type WidgetStore } from "../state/widget-store.ts";
import type {
  BootstrapFailureReason,
  BootstrapScriptElement,
} from "./bootstrap-source.ts";
import { parseBootstrapSource } from "./bootstrap-source.ts";

type FetchPublicWidgetConfigFailure = Extract<
  FetchPublicWidgetConfigResult,
  { ok: false }
>;

export type WidgetRoot = {
  replaceChildren(...nodes: Node[]): void;
};

export type BootstrapWidgetInput = {
  apiBaseUrl?: string;
  env?: WidgetEnvInput;
  fetchConfig?: (
    input: Omit<FetchPublicWidgetConfigInput, "fetchFn">,
  ) => Promise<FetchPublicWidgetConfigResult>;
  hostname?: string | null;
  root?: WidgetRoot | null;
  scriptElement?: BootstrapScriptElement | null;
  store?: WidgetStore;
  timeoutMs?: number;
};

export type BootstrapWidgetResult =
  | {
      config: RuntimeWidgetConfig;
      ok: true;
    }
  | {
      ok: false;
      reason: BootstrapFailureReason | FetchPublicWidgetConfigFailure["reason"];
    };

function clearRoot(root: WidgetRoot | null | undefined) {
  root?.replaceChildren();
}

function resolveApiBaseUrl(input: BootstrapWidgetInput) {
  if (input.apiBaseUrl) {
    return parseWidgetEnv({ VITE_WIDGET_API_BASE_URL: input.apiBaseUrl });
  }

  return parseWidgetEnv(input.env ?? {});
}

export async function bootstrapWidget(
  input: BootstrapWidgetInput,
): Promise<BootstrapWidgetResult> {
  const store = input.store ?? createWidgetStore();
  const envResult = resolveApiBaseUrl(input);
  if (!envResult.ok) {
    clearRoot(input.root);
    store.setFailed(envResult.reason);
    return envResult;
  }

  const sourceResult = parseBootstrapSource({
    hostname: input.hostname,
    scriptElement: input.scriptElement,
  });
  if (!sourceResult.ok) {
    clearRoot(input.root);
    store.setFailed(sourceResult.reason);
    return sourceResult;
  }

  store.setLoading();

  const fetchConfig = input.fetchConfig ?? fetchPublicWidgetConfig;
  const configResult = await fetchConfig({
    apiBaseUrl: envResult.apiBaseUrl,
    domain: sourceResult.hostname,
    projectApiKey: sourceResult.projectApiKey,
    timeoutMs: input.timeoutMs,
  });

  if (!configResult.ok) {
    clearRoot(input.root);
    store.setFailed(configResult.reason);
    return configResult;
  }

  store.setReady(configResult.config);
  return configResult;
}
